import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "./utils/supabaseClient";
import { useAuth } from "./providers/AuthProvider";
import {
    Box,
    Button,
    Heading,
    Card,
    HStack,
    Badge,
    Text,
    VStack,
    Container,
    Flex,
    Icon,
    Divider,
    SimpleGrid,
    Avatar,
    Spinner,
    Center,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import moment from "moment";
import { FiUser, FiMail, FiPhone, FiCalendar, FiMapPin, FiCreditCard, FiAward, FiBookOpen, FiCheckCircle, FiEye, FiX } from "react-icons/fi";
import { isSupersededInvoice } from "./utils/formatHelpers";
import {
    getStaffPreviewAccess,
    getStoredViewAsStudent,
    clearStoredViewAsStudent,
} from "./utils/staffHelpers";
import { COLORS } from "./styles/colors";
import { getPhotoUrlFromAdditionalData } from "./utils/profilePhoto";

export const Profile = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [profileData, setProfileData] = useState(null);
    const [registrations, setRegistrations] = useState([]);
    const [certifications, setCertifications] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [viewAsStudent, setViewAsStudent] = useState(null);
    const [previewReady, setPreviewReady] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const init = async () => {
            setPreviewReady(false);
            if (!user?.email && !user?.id) {
                setViewAsStudent(null);
                if (!cancelled) setPreviewReady(true);
                return;
            }

            const stored = getStoredViewAsStudent();
            if (stored && user?.id) {
                const { canPreview } = await getStaffPreviewAccess(user.id);
                if (cancelled) return;
                if (canPreview) {
                    setViewAsStudent(stored);
                } else {
                    clearStoredViewAsStudent();
                    setViewAsStudent(null);
                }
            } else {
                setViewAsStudent(null);
            }
            if (!cancelled) setPreviewReady(true);
        };
        init();
        return () => { cancelled = true; };
    }, [user?.id, user?.email]);

    useEffect(() => {
        if (!previewReady) return;
        if (!user?.email && !user?.id) return;
        fetchProfileData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [previewReady, user?.email, user?.id, viewAsStudent?.value]);

    const exitStudentPreview = () => {
        clearStoredViewAsStudent();
        setViewAsStudent(null);
    };

    const fetchProfileData = async () => {
        try {
            setLoading(true);

            const targetId = viewAsStudent?.userId || (!viewAsStudent ? user?.id : null);
            const targetEmail = viewAsStudent?.email || (!viewAsStudent ? user?.email : null);

            if (!targetEmail && !targetId) {
                setProfileData(null);
                setLoading(false);
                return;
            }

            // Fetch latest registration for profile data - try both user_id and email
            let registrationData = [];
            let regError = null;

            // First, try to fetch by user_id if available
            if (targetId) {
                const { data: dataById, error: errorById } = await supabase
                    .from("registration")
                    .select("*")
                    .eq("user_id", targetId)
                    .order("created_at", { ascending: false })
                    .limit(1);
                
                if (!errorById && dataById && dataById.length > 0) {
                    registrationData = dataById;
                } else {
                    regError = errorById;
                }
            }
            
            // If no results by user_id, try by email (case-insensitive)
            if (registrationData.length === 0 && targetEmail) {
                const { data: dataByEmail, error: errorByEmail } = await supabase
                    .from("registration")
                    .select("*")
                    .ilike("participant_email", targetEmail)
                    .order("created_at", { ascending: false })
                    .limit(1);
                
                if (!errorByEmail && dataByEmail && dataByEmail.length > 0) {
                    registrationData = dataByEmail;
                } else if (errorByEmail) {
                    regError = errorByEmail;
                }
            }

            if (regError) throw regError;

            if (registrationData && registrationData.length > 0) {
                setProfileData(registrationData[0]);

                // Fetch all registrations by user_id AND email, then merge by id
                // so enrollments not yet linked to auth still appear.
                const allRegsSelect = `
                            *,
                            draft_cert_run_code,
                            full_certification_runs (
                                id,
                                run_name,
                                start_date,
                                end_date,
                                full_certification (
                                    name,
                                    description
                                )
                            )
                        `;
                const allRegsById = new Map();
                let allRegError = null;

                if (targetId) {
                    const { data: regsById, error: errorById } = await supabase
                        .from("registration")
                        .select(allRegsSelect)
                        .eq("user_id", targetId)
                        .order("created_at", { ascending: false });

                    if (errorById) {
                        allRegError = errorById;
                    } else {
                        (regsById || []).forEach((r) => allRegsById.set(r.id, r));
                    }
                }

                if (targetEmail) {
                    const { data: regsByEmail, error: errorByEmail } = await supabase
                        .from("registration")
                        .select(allRegsSelect)
                        .ilike("participant_email", targetEmail)
                        .order("created_at", { ascending: false });

                    if (errorByEmail) {
                        allRegError = errorByEmail;
                    } else {
                        (regsByEmail || []).forEach((r) => {
                            if (!allRegsById.has(r.id)) allRegsById.set(r.id, r);
                        });
                    }
                }

                if (allRegError) throw allRegError;
                const allRegs = Array.from(allRegsById.values())
                    .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
                const registrationIds = allRegs.map((r) => r.id);
                setRegistrations(allRegs || []);

                // Fetch invoices for all registrations by registration_id
                console.log("Registration IDs for invoice lookup:", registrationIds);
                
                if (registrationIds.length > 0) {
                    const { data: invoiceData, error: invoiceError } = await supabase
                        .from("invoices")
                        .select("*, invoice_line_items(*)")
                        .in("registration_id", registrationIds)
                        .order("due_date", { ascending: true, nullsLast: true });

                    if (invoiceError) {
                        console.error("Error fetching invoices:", invoiceError);
                    } else {
                        console.log("Fetched invoices:", invoiceData);
                        // Sort by due_date (nearest first), then by invoice_date if due_date is null
                        const sortedInvoices = (invoiceData || []).sort((a, b) => {
                            const aDueDate = a.due_date ? moment(a.due_date) : null;
                            const bDueDate = b.due_date ? moment(b.due_date) : null;
                            
                            if (aDueDate && bDueDate) {
                                return aDueDate.diff(bDueDate);
                            } else if (aDueDate) {
                                return -1; // a has due_date, b doesn't - a comes first
                            } else if (bDueDate) {
                                return 1; // b has due_date, a doesn't - b comes first
                            } else {
                                // Both null, sort by invoice_date descending
                                const aDate = a.invoice_date ? moment(a.invoice_date) : moment(0);
                                const bDate = b.invoice_date ? moment(b.invoice_date) : moment(0);
                                return bDate.diff(aDate);
                            }
                        });
                        setInvoices(sortedInvoices);
                    }
                } else {
                    console.log("No registration IDs found, cannot fetch invoices");
                }
            } else {
                setProfileData(null);
                setRegistrations([]);
                setInvoices([]);
                setCertifications([]);
            }

            // Fetch certifications
            const { data: certData, error: certError } = await supabase
                .from("full_certification_runs")
                .select(`
                    *,
                    full_certification (
                        name,
                        description,
                        cost
                    )
                `)
                .in("id", 
                    registrationData
                        ?.filter(r => r.draft_cert_run_code)
                        .map(r => r.draft_cert_run_code) || []
                );

            if (certError) throw certError;
            setCertifications(certData || []);
        } catch (error) {
            console.error("Error fetching profile data:", error);
        } finally {
            setLoading(false);
        }
    };

    const previewBanner = viewAsStudent ? (
        <Box
            mb={{ base: 3, md: 4 }}
            p={{ base: 3, md: 4 }}
            borderRadius="xl"
            bg="orange.50"
            border="1px solid"
            borderColor="orange.200"
        >
            <Flex
                direction={{ base: "column", sm: "row" }}
                align={{ base: "stretch", sm: "center" }}
                gap={3}
                justify="space-between"
            >
                <HStack spacing={2.5} align="flex-start" minW={0}>
                    <Icon as={FiEye} color="orange.500" boxSize={4} mt={0.5} flexShrink={0} />
                    <Box minW={0}>
                        <HStack spacing={2} mb={0.5} flexWrap="wrap">
                            <Text fontSize={{ base: "xs", md: "sm" }} fontWeight="700" color="gray.800">
                                Previewing student profile
                            </Text>
                            <Badge colorScheme="orange" fontSize="2xs" borderRadius="md" textTransform="none">
                                Read-only
                            </Badge>
                        </HStack>
                        <Text fontSize={{ base: "2xs", md: "xs" }} color="gray.600" noOfLines={2}>
                            {viewAsStudent.name}
                            {viewAsStudent.email ? ` (${viewAsStudent.email})` : ""}
                        </Text>
                    </Box>
                </HStack>
                <Button
                    size="sm"
                    variant="solid"
                    bg={COLORS.maastrichtBlue}
                    color="white"
                    leftIcon={<Icon as={FiX} boxSize={3.5} />}
                    onClick={exitStudentPreview}
                    borderRadius="10px"
                    _hover={{ bg: "#162447" }}
                    flexShrink={0}
                >
                    Exit preview
                </Button>
            </Flex>
        </Box>
    ) : null;

    if (loading || !previewReady) {
        return (
            <Center minH="100vh">
                <Spinner size="xl" />
            </Center>
        );
    }

    if (!profileData) {
        return (
            <Box minH="100vh" py={{ base: 4, md: 8 }}>
                <Container maxW="container.lg" px={{ base: 4, md: 6 }}>
                    {previewBanner}
                    <Button 
                        variant="outline" 
                        mb={5} 
                        leftIcon={<Icon as={FiBookOpen} />} 
                        onClick={() => navigate("/enrolled-courses")}
                        size={{ base: "sm", md: "md" }}
                    >
                        Back To Courses
                    </Button>
                    <Center py={12}>
                        <VStack spacing={4}>
                            <Text fontSize={{ base: "md", md: "lg" }} color="gray.600">
                                No profile information found.
                            </Text>
                            <Button onClick={() => navigate("/enrolled-courses")} size={{ base: "sm", md: "md" }}>
                                View My Courses
                            </Button>
                        </VStack>
                    </Center>
                </Container>
            </Box>
        );
    }

    // Parse additional_data if it's a string
    let additionalInfo = {};
    if (profileData.additional_data) {
        try {
            additionalInfo = typeof profileData.additional_data === 'string' 
                ? JSON.parse(profileData.additional_data) 
                : profileData.additional_data;
        } catch (e) {
            console.error('Error parsing additional_data:', e);
        }
    }

    const displayEmail =
        profileData.participant_email ||
        viewAsStudent?.email ||
        user?.email;

    return (
        <Box minH="100vh" py={{ base: 4, md: 8 }} bg="gray.50">
            <Container maxW="container.xl" px={{ base: 4, md: 6 }}>
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <VStack spacing={{ base: 4, md: 6 }} align="stretch">
                        {previewBanner}
                        {/* Header */}
                        <Box bg="white" p={{ base: 4, md: 8 }} borderRadius="lg" boxShadow="md">
                            <Flex 
                                direction={{ base: "column", md: "row" }} 
                                justify="space-between" 
                                align={{ base: "flex-start", md: "center" }}
                                mb={6}
                                gap={4}
                            >
                                <HStack spacing={4}>
                                    <Avatar
                                        size={{ base: "lg", md: "xl" }}
                                        name={profileData.participant_name || displayEmail || "User"}
                                        src={getPhotoUrlFromAdditionalData(additionalInfo) || user?.user_metadata?.avatar_url}
                                        bg="brand.500"
                                        color="white"
                                    />
                                    <Box>
                                        <Heading size={{ base: "md", md: "lg" }} mb={1}>
                                            {profileData.participant_name || "User"}
                                        </Heading>
                                        <Text color="gray.600" fontSize={{ base: "sm", md: "md" }}>{displayEmail}</Text>
                                    </Box>
                                </HStack>
                                <Button 
                                    variant="outline" 
                                    leftIcon={<Icon as={FiBookOpen} />}
                                    onClick={() => navigate("/enrolled-courses")}
                                    size={{ base: "sm", md: "md" }}
                                    w={{ base: "100%", md: "auto" }}
                                >
                                    My Courses
                                </Button>
                            </Flex>

                            <Divider my={6} />

                            {/* Profile Information */}
                            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={{ base: 4, md: 6 }}>
                                {profileData.participant_name && (
                                    <Box>
                                        <HStack mb={2}>
                                            <Icon as={FiUser} color="blue.500" />
                                            <Text fontWeight="600" fontSize="sm" color="gray.500">
                                                Full Name
                                            </Text>
                                        </HStack>
                                        <Text fontSize="md" fontWeight="500">
                                            {profileData.participant_name}
                                        </Text>
                                    </Box>
                                )}

                                {profileData.participant_email && (
                                    <Box>
                                        <HStack mb={2}>
                                            <Icon as={FiMail} color="green.500" />
                                            <Text fontWeight="600" fontSize="sm" color="gray.500">
                                                Email
                                            </Text>
                                        </HStack>
                                        <Text fontSize="md" fontWeight="500">
                                            {profileData.participant_email}
                                        </Text>
                                    </Box>
                                )}

                                {profileData.mobile_no && (
                                    <Box>
                                        <HStack mb={2}>
                                            <Icon as={FiPhone} color="purple.500" />
                                            <Text fontWeight="600" fontSize="sm" color="gray.500">
                                                Mobile
                                            </Text>
                                        </HStack>
                                        <Text fontSize="md" fontWeight="500">
                                            {profileData.mobile_no}
                                        </Text>
                                    </Box>
                                )}

                                {profileData.nric && (
                                    <Box>
                                        <HStack mb={2}>
                                            <Icon as={FiUser} color="orange.500" />
                                            <Text fontWeight="600" fontSize="sm" color="gray.500">
                                                NRIC/FIN
                                            </Text>
                                        </HStack>
                                        <Text fontSize="md" fontWeight="500">
                                            {profileData.nric}
                                        </Text>
                                    </Box>
                                )}

                                {profileData.dob && (
                                    <Box>
                                        <HStack mb={2}>
                                            <Icon as={FiCalendar} color="red.500" />
                                            <Text fontWeight="600" fontSize="sm" color="gray.500">
                                                Date of Birth
                                            </Text>
                                        </HStack>
                                        <Text fontSize="md" fontWeight="500">
                                            {moment(profileData.dob).format("DD MMM YYYY")}
                                        </Text>
                                    </Box>
                                )}

                                {profileData.nationality && (
                                    <Box>
                                        <HStack mb={2}>
                                            <Icon as={FiMapPin} color="teal.500" />
                                            <Text fontWeight="600" fontSize="sm" color="gray.500">
                                                Nationality
                                            </Text>
                                        </HStack>
                                        <Text fontSize="md" fontWeight="500">
                                            {profileData.nationality}
                                        </Text>
                                    </Box>
                                )}
                            </SimpleGrid>

                            {profileData.sponsor_uen && (
                                <>
                                    <Divider my={6} />
                                    <Box>
                                        <Text fontWeight="600" mb={3} fontSize="sm" color="gray.500">
                                            Organization Details
                                        </Text>
                                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                                            <Box>
                                                <Text fontSize="xs" color="gray.500">Sponsor UEN</Text>
                                                <Text fontSize="md" fontWeight="500">
                                                    {profileData.sponsor_uen}
                                                </Text>
                                            </Box>
                                            {profileData.is_sme !== null && (
                                                <Box>
                                                    <Text fontSize="xs" color="gray.500">SME Status</Text>
                                                    <Badge colorScheme={profileData.is_sme ? "green" : "gray"} fontSize="sm">
                                                        {profileData.is_sme ? "SME" : "Non-SME"}
                                                    </Badge>
                                                </Box>
                                            )}
                                        </SimpleGrid>
                                    </Box>
                                </>
                            )}
                        </Box>

                        {/* Invoices */}
                        {invoices.length > 0 && (
                            <Box bg="white" p={{ base: 4, md: 8 }} borderRadius="lg" boxShadow="md">
                                <Heading 
                                    size={{ base: "sm", md: "md" }} 
                                    mb={{ base: 4, md: 6 }} 
                                    pb={3} 
                                    borderBottom="2px solid" 
                                    borderColor="brand.400"
                                    fontSize={{ base: "lg", md: "xl" }}
                                >
                                    <Icon as={FiCreditCard} mr={2} />My Invoices
                                </Heading>
                                <VStack spacing={{ base: 3, md: 4 }} align="stretch">
                                    {invoices.map((invoice) => {
                                        const isPaid = invoice.status === "paid" || invoice.status === "Paid";
                                        return (
                                        <Card 
                                            key={invoice.id} 
                                            p={{ base: 3, md: 4 }} 
                                            borderWidth="2px" 
                                            borderColor={isPaid ? "green.300" : "gray.200"}
                                            bg={isPaid ? "green.50" : "white"}
                                            borderRadius="md"
                                            _hover={{ shadow: "md" }}
                                            transition="all 0.2s"
                                        >
                                            <VStack align="stretch" spacing={3}>
                                                <Flex 
                                                    direction={{ base: "column", sm: "row" }} 
                                                    justify="space-between" 
                                                    align={{ base: "flex-start", sm: "center" }}
                                                    gap={2}
                                                >
                                                    <Box flex="1">
                                                        <HStack spacing={2} mb={1} flexWrap="wrap">
                                                            <Text fontWeight="600" fontSize={{ base: "sm", md: "md" }}>
                                                                {invoice.invoice_number || `Invoice #${invoice.id.substring(0, 8)}`}
                                                            </Text>
                                                            <Badge 
                                                                colorScheme={
                                                                    invoice.status === "paid" || invoice.status === "Paid" ? "green" :
                                                                    invoice.status === "pending" ? "yellow" :
                                                                    invoice.status === "failed" ? "red" :
                                                                    "gray"
                                                                }
                                                                fontSize={{ base: "xs", md: "sm" }}
                                                                variant={isPaid ? "solid" : "subtle"}
                                                            >
                                                                {isPaid ? "✓ Paid" : (invoice.status || "Pending")}
                                                            </Badge>
                                                        </HStack>
                                                        <VStack align="flex-start" spacing={1}>
                                                            {invoice.due_date && (
                                                                <Text fontSize={{ base: "xs", md: "sm" }} color="gray.600">
                                                                    Due Date: {moment(invoice.due_date).format("DD MMM YYYY")}
                                                                </Text>
                                                            )}
                                                            {invoice.invoice_date && (
                                                                <Text fontSize={{ base: "xs", md: "sm" }} color="gray.600">
                                                                    Invoice Date: {moment(invoice.invoice_date).format("DD MMM YYYY")}
                                                                </Text>
                                                            )}
                                                        </VStack>
                                                    </Box>
                                                    <VStack align={{ base: "flex-start", sm: "flex-end" }} spacing={1}>
                                                        <Text fontSize={{ base: "xs", md: "sm" }} color="gray.500">
                                                            Total Amount
                                                        </Text>
                                                        <Text 
                                                            fontSize={{ base: "lg", md: "xl" }} 
                                                            fontWeight="bold" 
                                                            color={isPaid ? "green.700" : "brand.700"}
                                                        >
                                                            ${(() => {
                                                                // Prioritize unit_price from invoices table (the price to pay)
                                                                if (invoice.unit_price && Number(invoice.unit_price) > 0) {
                                                                    return Number(invoice.unit_price).toFixed(2);
                                                                } 
                                                                // Fall back to total_amount
                                                                else if (invoice.total_amount && Number(invoice.total_amount) > 0) {
                                                                    return Number(invoice.total_amount).toFixed(2);
                                                                } 
                                                                // Last resort: calculate from line items
                                                                else if (invoice.invoice_line_items && invoice.invoice_line_items.length > 0) {
                                                                    const calculated = invoice.invoice_line_items.reduce((sum, item) => 
                                                                        sum + (Number(item.unit_price || item.amount || 0) * Number(item.quantity || 1)), 0
                                                                    );
                                                                    return calculated > 0 ? calculated.toFixed(2) : "0.00";
                                                                }
                                                                return "0.00";
                                                            })()}
                                                        </Text>
                                                        {invoice.amount_paid && invoice.amount_paid > 0 && (
                                                            <Text fontSize={{ base: "xs", md: "sm" }} color="green.600">
                                                                Paid: ${Number(invoice.amount_paid).toFixed(2)}
                                                            </Text>
                                                        )}
                                                    </VStack>
                                                </Flex>
                                                {invoice.payment_date && (
                                                    <Text fontSize={{ base: "xs", md: "sm" }} color="gray.600">
                                                        Payment Date: {moment(invoice.payment_date).format("DD MMM YYYY")}
                                                    </Text>
                                                )}
                                                {invoice.invoice_line_items && invoice.invoice_line_items.length > 0 && (
                                                    <Box mt={2} pt={2} borderTop="1px solid" borderColor="gray.200">
                                                        <Text fontSize={{ base: "xs", md: "sm" }} fontWeight="600" mb={2} color="gray.700">
                                                            Line Items:
                                                        </Text>
                                                        <VStack align="stretch" spacing={1}>
                                                            {invoice.invoice_line_items.map((item, idx) => (
                                                                <HStack key={idx} justify="space-between" fontSize={{ base: "xs", md: "sm" }}>
                                                                    <Text color="gray.600">
                                                                        {item.description || item.item_name || "Item"}
                                                                        {item.quantity && item.quantity > 1 && ` (x${item.quantity})`}
                                                                    </Text>
                                                                    <Text fontWeight="600">
                                                                        ${(() => {
                                                                            // Use unit_price * quantity for line items
                                                                            const unitPrice = Number(item.unit_price || item.amount || 0);
                                                                            const quantity = Number(item.quantity || 1);
                                                                            return (unitPrice * quantity).toFixed(2);
                                                                        })()}
                                                                    </Text>
                                                                </HStack>
                                                            ))}
                                                        </VStack>
                                                    </Box>
                                                )}
                                            </VStack>
                                        </Card>
                                        );
                                    })}
                                </VStack>
                            </Box>
                        )}

                        {/* Outstanding Balance - Dynamic from unpaid invoices */}
                        {(() => {
                            // Calculate outstanding balance from unpaid invoices
                            const unpaidInvoices = invoices.filter(inv =>
                                !isSupersededInvoice(inv) &&
                                inv.status !== "paid" && inv.status !== "Paid"
                            );
                            
                            const outstandingBalance = unpaidInvoices.reduce((sum, invoice) => {
                                let amount = 0;
                                if (invoice.unit_price && Number(invoice.unit_price) > 0) {
                                    amount = Number(invoice.unit_price);
                                } else if (invoice.total_amount && Number(invoice.total_amount) > 0) {
                                    amount = Number(invoice.total_amount);
                                } else if (invoice.invoice_line_items && invoice.invoice_line_items.length > 0) {
                                    amount = invoice.invoice_line_items.reduce((itemSum, item) => 
                                        itemSum + (Number(item.unit_price || item.amount || 0) * Number(item.quantity || 1)), 0
                                    );
                                }
                                return sum + amount;
                            }, 0);

                            // Only show if there are unpaid invoices or if profileData has payment_balance
                            if (unpaidInvoices.length > 0 || (profileData.payment_balance !== null && profileData.payment_balance !== undefined)) {
                                return (
                                    <Box bg="white" p={{ base: 4, md: 8 }} borderRadius="lg" boxShadow="md">
                                        <Heading 
                                            size={{ base: "sm", md: "md" }} 
                                            mb={{ base: 4, md: 6 }} 
                                            pb={3} 
                                            borderBottom="2px solid" 
                                            borderColor="brand.400"
                                            fontSize={{ base: "lg", md: "xl" }}
                                        >
                                            <Icon as={FiCreditCard} mr={2} />Outstanding Balance
                                        </Heading>
                                        <Box>
                                            <Text fontSize={{ base: "xs", md: "sm" }} color="gray.500" mb={1}>
                                                Total Outstanding
                                            </Text>
                                            <Text 
                                                fontSize={{ base: "2xl", md: "3xl" }} 
                                                fontWeight="bold" 
                                                color={outstandingBalance > 0 ? "red.500" : "green.500"}
                                            >
                                                ${outstandingBalance.toFixed(2)}
                                            </Text>
                                            {unpaidInvoices.length > 0 && (
                                                <Text fontSize={{ base: "xs", md: "sm" }} color="gray.600" mt={2}>
                                                    {unpaidInvoices.length} {unpaidInvoices.length === 1 ? "invoice" : "invoices"} pending payment
                                                </Text>
                                            )}
                                        </Box>
                                    </Box>
                                );
                            }
                            return null;
                        })()}

                        {/* Registrations/Courses */}
                        {registrations.length > 0 && (
                            <Box bg="white" p={{ base: 4, md: 8 }} borderRadius="lg" boxShadow="md">
                                <Heading 
                                    size={{ base: "sm", md: "md" }} 
                                    mb={{ base: 4, md: 6 }} 
                                    pb={3} 
                                    borderBottom="2px solid" 
                                    borderColor="brand.400"
                                    fontSize={{ base: "lg", md: "xl" }}
                                >
                                    <Icon as={FiBookOpen} mr={2} />My Registrations
                                </Heading>
                                <VStack spacing={{ base: 3, md: 4 }} align="stretch">
                                    {registrations.map((reg) => (
                                        <Card 
                                            key={reg.id} 
                                            p={{ base: 3, md: 4 }} 
                                            borderWidth="1px" 
                                            borderColor="gray.200" 
                                            borderRadius="md"
                                        >
                                            <Flex 
                                                direction={{ base: "column", sm: "row" }} 
                                                justify="space-between" 
                                                align={{ base: "flex-start", sm: "start" }}
                                                gap={3}
                                            >
                                                <VStack align="start" spacing={2} flex="1">
                                                    {reg.draft_cert_run_code ? (
                                                        <>
                                                            <Text fontWeight="600" fontSize={{ base: "xs", md: "sm" }} color="gray.500">
                                                                CERTIFICATION PROGRAM
                                                            </Text>
                                                            <Text fontSize={{ base: "md", md: "lg" }} fontWeight="600">
                                                                {reg.full_certification_runs?.full_certification?.name || "Certification"}
                                                            </Text>
                                                            {reg.full_certification_runs?.start_date && (
                                                                <Text fontSize={{ base: "xs", md: "sm" }} color="gray.600">
                                                                    {moment(reg.full_certification_runs.start_date).format("DD MMM YYYY")} - {moment(reg.full_certification_runs.end_date).format("DD MMM YYYY")}
                                                                </Text>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Text fontWeight="600" fontSize={{ base: "xs", md: "sm" }} color="gray.500">
                                                                {reg.draft_course_run_code || reg.course_run_id ? "INDIVIDUAL COURSE" : "REGISTRATION"}
                                                            </Text>
                                                            <Text fontSize={{ base: "md", md: "lg" }} fontWeight="600">
                                                                {reg.participant_name || "Registration"}
                                                            </Text>
                                                            {reg.created_at && (
                                                                <Text fontSize={{ base: "xs", md: "sm" }} color="gray.600">
                                                                    Registered: {moment(reg.created_at).format("DD MMM YYYY")}
                                                                </Text>
                                                            )}
                                                        </>
                                                    )}
                                                    
                                                    <HStack mt={2} flexWrap="wrap" gap={2}>
                                                        <Badge 
                                                            colorScheme={
                                                                reg.registration_stage === "confirmed" ? "green" :
                                                                reg.registration_stage === "pending" ? "yellow" :
                                                                "gray"
                                                            }
                                                            fontSize={{ base: "xs", md: "sm" }}
                                                        >
                                                            {reg.registration_stage || "Pending"}
                                                        </Badge>
                                                        {reg.is_assessed && (
                                                            <Badge colorScheme="orange" fontSize={{ base: "xs", md: "sm" }}>
                                                                Assessed
                                                            </Badge>
                                                        )}
                                                    </HStack>
                                                </VStack>
                                                <Text fontSize={{ base: "xs", md: "sm" }} color="gray.500" whiteSpace="nowrap">
                                                    {moment(reg.created_at).format("DD MMM YYYY")}
                                                </Text>
                                            </Flex>
                                        </Card>
                                    ))}
                                </VStack>
                            </Box>
                        )}

                        {/* Payment Information - Discounts only (Outstanding Balance shown above) */}
                        {profileData.discount_amt !== null && profileData.discount_amt > 0 && (
                            <Box bg="white" p={{ base: 4, md: 8 }} borderRadius="lg" boxShadow="md">
                                <Heading 
                                    size={{ base: "sm", md: "md" }} 
                                    mb={{ base: 4, md: 6 }} 
                                    pb={3} 
                                    borderBottom="2px solid" 
                                    borderColor="brand.400"
                                    fontSize={{ base: "lg", md: "xl" }}
                                >
                                    <Icon as={FiCreditCard} mr={2} />Payment Information
                                </Heading>
                                <Box>
                                    <Text fontSize={{ base: "xs", md: "sm" }} color="gray.500" mb={1}>Total Discounts</Text>
                                    <Text fontSize={{ base: "2xl", md: "3xl" }} fontWeight="bold" color="green.500">
                                        ${profileData.discount_amt.toFixed(2)}
                                    </Text>
                                </Box>
                            </Box>
                        )}

                        {/* SkillsFuture Information */}
                        {profileData.skillsfuture_claim_id && (
                            <Box bg="white" p={{ base: 4, md: 8 }} borderRadius="lg" boxShadow="md">
                                <Heading 
                                    size={{ base: "sm", md: "md" }} 
                                    mb={{ base: 4, md: 6 }} 
                                    pb={3} 
                                    borderBottom="2px solid" 
                                    borderColor="brand.400"
                                    fontSize={{ base: "lg", md: "xl" }}
                                >
                                    <Icon as={FiAward} mr={2} />SkillsFuture Claim
                                </Heading>
                                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 4, md: 6 }}>
                                    <Box>
                                        <Text fontSize="xs" color="gray.500" mb={1}>Claim ID</Text>
                                        <Text fontSize="md" fontWeight="600">
                                            {profileData.skillsfuture_claim_id}
                                        </Text>
                                    </Box>
                                    {profileData.skillsfuture_claimed_amount !== null && (
                                        <Box>
                                            <Text fontSize="xs" color="gray.500" mb={1}>Claimed Amount</Text>
                                            <Text fontSize="md" fontWeight="600" color="green.500">
                                                ${profileData.skillsfuture_claimed_amount}
                                            </Text>
                                        </Box>
                                    )}
                                </SimpleGrid>
                            </Box>
                        )}
                    </VStack>
                </motion.div>
            </Container>
        </Box>
    );
};

